import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Upload, X, Check, RefreshCw } from "lucide-react";

// Compress any image File/Blob to an 85% JPEG, downscaled so the longest side
// is <= maxDim (default 800px) — keeps mobile camera uploads small & fast.
// Mobile-safe: if canvas encoding is unavailable or fails (older mobile
// WebViews, HEIC, etc.) it falls back to the original file so the server-side
// downscaler can still take over — the upload never silently sends 0 bytes.
function dataUrlToBlob(durl) {
  const [head, b64] = durl.split(",");
  const mime = (head.match(/:(.*?);/) || [])[1] || "image/jpeg";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export async function compressToJpeg(fileOrBlob, quality = 0.85, maxDim = 800) {
  try {
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej;
      r.readAsDataURL(fileOrBlob);
    });
    const img = await new Promise((res, rej) => {
      const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl;
    });
    let w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return fileOrBlob;
    if (Math.max(w, h) > maxDim) {
      const s = maxDim / Math.max(w, h);
      w = Math.round(w * s); h = Math.round(h * s);
    }
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => {
      try { canvas.toBlob(res, "image/jpeg", quality); } catch { res(null); }
    });
    if (blob && blob.size > 0) return blob;
    // toBlob unsupported/failed — fall back to toDataURL encoding.
    const b = dataUrlToBlob(canvas.toDataURL("image/jpeg", quality));
    if (b && b.size > 0) return b;
  } catch { /* fall through to original */ }
  return fileOrBlob;  // last resort — server will downscale & re-encode
}

// Auth-aware thumbnail: absolute URL used directly, else fetched as a blob.
function IdThumb({ url }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let revoke;
    if (!url) { setSrc(""); return; }
    if (/^https?:\/\//.test(url)) { setSrc(url); return; }
    api.get(`/files/serve/${url}`, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); setSrc(u); revoke = u; })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [url]);
  if (!src) return null;
  return <img src={src} alt="doc" className="mt-2 w-24 h-24 object-cover border-2 border-black rounded" />;
}

// Reusable dual-input (file/mobile-capture + desktop camera modal) with 85% JPEG
// compression and upload to blob storage. Calls onUploaded(fieldName, url).
export default function StudentIdCapture({ studentId, fieldName, label, value, onUploaded, uploadPath, schoolId }) {
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [shot, setShot] = useState(null);      // { blob, preview } of the captured frame awaiting confirmation
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const endpoint = uploadPath || `/students/${studentId}/id-document`;

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());  // release hardware
      streamRef.current = null;
    }
  };
  useEffect(() => () => stopStream(), []);

  // Compress → upload → persist via parent. Returns true only when the record is saved.
  const doUpload = async (blob) => {
    setBusy(true);
    try {
      const jpeg = await compressToJpeg(blob, 0.85);
      const fd = new FormData();
      fd.append("field_name", fieldName);
      if (schoolId) fd.append("school_id", schoolId);
      fd.append("file", new File([jpeg], `${fieldName}.jpg`, { type: "image/jpeg" }));
      const r = await api.post(endpoint, fd);
      await onUploaded(fieldName, r.data.url);   // wait for parent patch + roster refresh
      toast.success(`${label} uploaded`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ""; };

  const openCamera = async () => {
    setShot(null);
    setCamOpen(true);
    try {
      let stream;
      try { stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } }); }
      catch { stream = await navigator.mediaDevices.getUserMedia({ video: true }); }
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
    } catch { toast.error("Camera unavailable — use Choose from Device"); closeCamera(); }
  };
  const closeCamera = () => {
    stopStream();
    if (shot?.preview) URL.revokeObjectURL(shot.preview);
    setShot(null);
    setCamOpen(false);
  };
  // Grab a frame and hold it for confirmation (keeps the modal open, releases the camera).
  const capture = async () => {
    const v = videoRef.current; if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return;
    stopStream();  // release hardware once we have the frame
    setShot({ blob, preview: URL.createObjectURL(blob) });
  };
  const retake = () => {
    if (shot?.preview) URL.revokeObjectURL(shot.preview);
    setShot(null);
    openCamera();
  };
  // Confirm the captured photo: save first, only close once the record is persisted.
  const confirmShot = async () => {
    if (!shot) return;
    const ok = await doUpload(shot.blob);
    if (ok) closeCamera();   // stay open on failure so the user can retry
  };

  return (
    <div className="block" data-testid={`idcap-${fieldName}`}>
      <span className="nb-label">{label}</span>
      <div className="flex flex-wrap gap-2 mt-1">
        <label className="nb-btn nb-btn-secondary text-xs cursor-pointer">
          <Upload size={14} />{busy ? "Uploading…" : "Choose from Device"}
          <input type="file" accept="image/*" className="hidden"
                 onChange={onFile} disabled={busy} data-testid={`idcap-file-${fieldName}`} />
        </label>
        <button type="button" onClick={openCamera} disabled={busy}
                className="nb-btn nb-btn-ghost text-xs" data-testid={`idcap-camera-${fieldName}`}>
          <Camera size={14} />Take Photo
        </button>
        {value && <span className="text-xs text-emerald-700 self-center">✓ on file</span>}
      </div>
      <IdThumb url={value} />

      {camOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
             data-testid={`idcap-modal-${fieldName}`}>
          <div className="nb-card bg-white p-4 w-full max-w-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="display font-bold">Capture {label}</span>
              <button onClick={closeCamera} disabled={busy} className="nb-btn nb-btn-ghost text-xs" data-testid={`idcap-close-${fieldName}`}><X size={14} /></button>
            </div>

            {!shot ? (
              <>
                <video ref={videoRef} playsInline muted className="w-full rounded-lg border-2 border-black bg-black aspect-video object-cover" />
                <div className="flex gap-2">
                  <button onClick={capture} className="nb-btn flex-1 justify-center" data-testid={`idcap-shoot-${fieldName}`}><Check size={16} />Capture</button>
                </div>
              </>
            ) : (
              <>
                <img src={shot.preview} alt="captured" className="w-full rounded-lg border-2 border-black bg-black aspect-video object-cover" data-testid={`idcap-preview-${fieldName}`} />
                <div className="flex gap-2">
                  <button onClick={confirmShot} disabled={busy} className="nb-btn flex-1 justify-center" data-testid={`idcap-confirm-${fieldName}`}>
                    <Check size={16} />{busy ? "Saving…" : "Use Photo & Save"}
                  </button>
                  <button onClick={retake} disabled={busy} className="nb-btn nb-btn-ghost" title="Retake" data-testid={`idcap-retake-${fieldName}`}><RefreshCw size={16} />Retake</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
